import { useState, useMemo } from 'react';
import { ArrowLeft, Camera, Loader2 } from 'lucide-react';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { COUNTRY_NAMES } from '../../utils/currency';

// Check if user is verified
const isUserVerified = (user) => {
  const status = user?.verification_status?.toUpperCase() || '';
  return status === 'VERIFIED';
};

// Helper to convert country name to code if needed
const getCountryCode = (value) => {
  if (!value) return '';
  // If it's already a valid country code, return it
  if (COUNTRY_NAMES[value]) return value;
  // Try to find the code by country name (case-insensitive)
  // Normalize by trimming and comparing lowercase
  const normalizedValue = value.trim().toLowerCase();
  const entry = Object.entries(COUNTRY_NAMES).find(
    ([, name]) => name.toLowerCase() === normalizedValue
  );
  console.log('getCountryCode - input:', value, 'normalized:', normalizedValue, 'found:', entry);
  return entry ? entry[0] : value;
};

export default function ProfileInfo({ onBack }) {
  const { profileForm, setProfileForm } = useAppState();
  const { user, updateProfile, isLoading, fetchUserData } = useAuth();
  const { isDesktop } = useResponsive();
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null
  const [isSaving, setIsSaving] = useState(false);

  // Check if user is verified - fields are editable when NOT verified
  const isVerified = useMemo(() => isUserVerified(user), [user]);
  const isEditable = !isVerified;

  // Helper to get value: prefer API user data, then fall back to local form
  const getValue = (field, apiField = field) => {
    // Check API user data FIRST (authoritative source)
    if (user) {
      // Handle snake_case API fields
      const snakeField = apiField.replace(/([A-Z])/g, '_$1').toLowerCase();

      // Special handling for phone - check multiple possible field names
      if (field === 'phone') {
        const phoneValue = user.phone || user.phone_number || user.mobile || user.identifier;
        if (phoneValue) return phoneValue;
      }

      // Special handling for country - convert name to code
      if (field === 'country') {
        const countryValue = user[snakeField] || user[apiField] || user[field];
        if (countryValue) return getCountryCode(countryValue);
      }

      const apiValue = user[snakeField] || user[apiField] || user[field];
      if (apiValue) return apiValue;
    }

    // Fall back to local form for unsaved changes or missing API data
    if (profileForm[field] !== undefined && profileForm[field] !== '') {
      return profileForm[field];
    }
    return '';
  };

  const handleChange = (field, value) => {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
    setSaveStatus(null); // Clear status when user makes changes
  };

  const handleSave = async () => {
    if (isSaving || !isEditable) return;

    try {
      setIsSaving(true);
      setSaveStatus(null);
      await updateProfile({
        firstName: getValue('firstName'),
        lastName: getValue('lastName'),
        email: getValue('email'),
        phone: getValue('phone'),
        dateOfBirth: getValue('dateOfBirth'),
        gender: getValue('gender'),
        country: getValue('country'),
      });

      // Refresh user data
      await fetchUserData();

      setSaveStatus('success');
      // Clear the status after 3 seconds
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (err) {
      console.error('Failed to save profile:', err);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  // Get display name for avatar
  const firstName = getValue('firstName') || 'User';
  const lastName = getValue('lastName') || '';

  return (
    <div className="flex-1 overflow-y-auto scrollbar-hide bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 bg-white z-10 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] flex items-center gap-3 border-b border-gray-100">
        <button onClick={onBack} className="p-2 text-gray-600 hover:bg-gray-100 rounded-full">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-lg font-bold text-gray-900">Profile Information</h1>
      </header>

      <div className={`px-4 py-6 ${isDesktop ? 'max-w-4xl mx-auto' : ''}`}>
        {/* Avatar - matching Menu page style */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div className="w-24 h-24 rounded-full p-[3px] bg-gradient-to-br from-sky-400 via-blue-500 to-indigo-600">
              <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                <span className="text-2xl font-bold text-indigo-700">
                  {firstName.charAt(0)}{lastName.charAt(0)}
                </span>
              </div>
            </div>
            <button className="absolute bottom-0 right-0 w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white shadow-lg">
              <Camera size={16} />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-sm p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
            <input
              type="text"
              value={getValue('firstName')}
              onChange={(e) => handleChange('firstName', e.target.value)}
              className={`input ${isEditable ? 'bg-white text-gray-900' : 'bg-gray-50 text-gray-500'}`}
              disabled={!isEditable}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
            <input
              type="text"
              value={getValue('lastName')}
              onChange={(e) => handleChange('lastName', e.target.value)}
              className={`input ${isEditable ? 'bg-white text-gray-900' : 'bg-gray-50 text-gray-500'}`}
              disabled={!isEditable}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={getValue('email')}
              onChange={(e) => handleChange('email', e.target.value)}
              className={`input ${isEditable ? 'bg-white text-gray-900' : 'bg-gray-50 text-gray-500'}`}
              disabled={!isEditable}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
            <input
              type="tel"
              value={getValue('phone')}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="+1 (555) 000-0000"
              className={`input ${isEditable ? 'bg-white text-gray-900' : 'bg-gray-50 text-gray-500'}`}
              disabled={!isEditable}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
            <input
              type="date"
              value={getValue('dateOfBirth')}
              onChange={(e) => handleChange('dateOfBirth', e.target.value)}
              className={`input text-left ${isEditable ? 'bg-white text-gray-900' : 'bg-gray-50 text-gray-500'}`}
              disabled={!isEditable}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
            <select
              value={getValue('gender')}
              onChange={(e) => handleChange('gender', e.target.value)}
              className={`input ${isEditable ? 'bg-white text-gray-900' : 'bg-gray-50 text-gray-500'}`}
              disabled={!isEditable}
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
              <option value="prefer_not_to_say">Prefer not to say</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
            <select
              value={getCountryCode(getValue('country'))}
              onChange={(e) => handleChange('country', e.target.value)}
              className={`input ${isEditable ? 'bg-white text-gray-900' : 'bg-gray-50 text-gray-500'}`}
              disabled={!isEditable}
            >
              <option value="">Select country</option>
              {Object.entries(COUNTRY_NAMES).map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Status Message */}
        {saveStatus === 'success' && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm text-center">
            Profile saved successfully!
          </div>
        )}
        {saveStatus === 'error' && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm text-center">
            Failed to save profile. Please try again.
          </div>
        )}

        {/* Save Button - enabled for non-verified users */}
        <button
          onClick={handleSave}
          disabled={!isEditable || isSaving}
          className={`w-full mt-4 py-4 font-bold rounded-2xl transition-colors flex items-center justify-center gap-2 ${
            isEditable && !isSaving
              ? 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-[0.98]'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {isSaving ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </div>
  );
}
